import React, { useState, useEffect, useCallback } from "react";
import { apiGet, apiPut } from "../utils/api";
import "../styles/shopReturnPolicy.css";

export default function ShopReturnPolicy() {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadPolicy = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiGet("/shop/return-policy");
      setPolicy(res?.returnPolicy || {
        acceptsReturns: true,
        returnWindowDays: 14,
        returnShippingPaidBy: "buyer",
        restockingFeePercent: 0,
        conditions: "",
        nonReturnableCategories: [],
      });
    } catch (err) {
      setError(err.message || "Failed to load return policy");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPolicy(); }, [loadPolicy]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const res = await apiPut("/shop/return-policy", policy);
      setPolicy(res?.returnPolicy || policy);
      setSuccess("Return policy saved successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const update = (key, value) => setPolicy((prev) => ({ ...prev, [key]: value }));

  if (loading) return <div className="srp-page page-responsive"><div className="srp-loading">Loading...</div></div>;

  return (
    <div className="srp-page page-responsive">
      <div className="srp-header">
        <div>
          <h1 className="srp-title">Return Policy</h1>
          <p className="srp-subtitle">Configure how returns and refunds work for your shop</p>
        </div>
      </div>

      {error && <div className="srp-error">⚠️ {error}</div>}
      {success && <div className="srp-success">✅ {success}</div>}

      {/* Accept Returns Toggle */}
      <div className="srp-section">
        <div className="srp-section-title">Returns</div>
        <div className="srp-toggle-row">
          <div>
            <div className="srp-label">Accept Returns</div>
            <div className="srp-hint">Allow customers to request returns on orders</div>
          </div>
          <label className="srp-switch">
            <input
              type="checkbox"
              checked={policy?.acceptsReturns !== false}
              onChange={(e) => update("acceptsReturns", e.target.checked)}
            />
            <span className="srp-slider" />
          </label>
        </div>
      </div>

      {policy?.acceptsReturns !== false && (
        <>
          {/* Return Window */}
          <div className="srp-section">
            <div className="srp-section-title">Return Window</div>
            <div className="srp-field">
              <label className="srp-label">Days after delivery</label>
              <div className="srp-hint">Customers can request a return within this many days after delivery</div>
              <div className="srp-number-row">
                <input
                  type="number"
                  className="srp-input srp-input-number"
                  value={policy?.returnWindowDays ?? 14}
                  onChange={(e) => update("returnWindowDays", Math.max(0, Math.min(90, Number(e.target.value) || 0)))}
                  min={0}
                  max={90}
                />
                <span className="srp-unit">days</span>
                <div className="srp-presets">
                  {[7, 14, 30, 60].map((d) => (
                    <button
                      key={d}
                      className={`srp-preset ${policy?.returnWindowDays === d ? "srp-preset-active" : ""}`}
                      onClick={() => update("returnWindowDays", d)}
                    >
                      {d}d
                    </button>
                  ))}
                </div>
              </div>
              <div className="srp-hint-note">
                💡 Your payout for an order is held until the return window ({policy?.returnWindowDays || 14} days) + 2 days after delivery, so every return is settled before you are paid.
              </div>
            </div>
          </div>

          {/* Return Shipping */}
          <div className="srp-section">
            <div className="srp-section-title">Return Shipping</div>
            <div className="srp-field">
              <label className="srp-label">Who pays return shipping?</label>
              <div className="srp-radio-group">
                <label className={`srp-radio ${policy?.returnShippingPaidBy === "buyer" ? "srp-radio-active" : ""}`}>
                  <input
                    type="radio"
                    name="returnShipping"
                    value="buyer"
                    checked={policy?.returnShippingPaidBy === "buyer"}
                    onChange={() => update("returnShippingPaidBy", "buyer")}
                  />
                  <div>
                    <div className="srp-radio-title">👤 Buyer pays</div>
                    <div className="srp-radio-desc">Customer is responsible for return shipping costs</div>
                  </div>
                </label>
                <label className={`srp-radio ${policy?.returnShippingPaidBy === "shop" ? "srp-radio-active" : ""}`}>
                  <input
                    type="radio"
                    name="returnShipping"
                    value="shop"
                    checked={policy?.returnShippingPaidBy === "shop"}
                    onChange={() => update("returnShippingPaidBy", "shop")}
                  />
                  <div>
                    <div className="srp-radio-title">🏪 Shop pays</div>
                    <div className="srp-radio-desc">You provide a prepaid return label (generated via ShipStation)</div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Restocking Fee */}
          <div className="srp-section">
            <div className="srp-section-title">Restocking Fee</div>
            <div className="srp-field">
              <label className="srp-label">Restocking fee percentage</label>
              <div className="srp-hint">Deducted from the refund amount (0% = no fee)</div>
              <div className="srp-number-row">
                <input
                  type="number"
                  className="srp-input srp-input-number"
                  value={policy?.restockingFeePercent ?? 0}
                  onChange={(e) => update("restockingFeePercent", Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  min={0}
                  max={100}
                />
                <span className="srp-unit">%</span>
                <div className="srp-presets">
                  {[0, 10, 15, 20].map((p) => (
                    <button
                      key={p}
                      className={`srp-preset ${policy?.restockingFeePercent === p ? "srp-preset-active" : ""}`}
                      onClick={() => update("restockingFeePercent", p)}
                    >
                      {p}%
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Conditions */}
          <div className="srp-section">
            <div className="srp-section-title">Conditions & Notes</div>
            <div className="srp-field">
              <label className="srp-label">Return conditions</label>
              <div className="srp-hint">Displayed to customers (e.g., "Items must be unused and in original packaging")</div>
              <textarea
                className="srp-textarea"
                value={policy?.conditions || ""}
                onChange={(e) => update("conditions", e.target.value)}
                placeholder="Items must be unused, unworn, and in original packaging with all tags attached. Custom or personalized items are non-returnable."
                maxLength={2000}
              />
              <div className="srp-char-count">{(policy?.conditions || "").length}/2000</div>
            </div>
          </div>
        </>
      )}

      {/* Save button */}
      <div className="srp-actions">
        <button className="srp-btn-save" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "💾 Save Return Policy"}
        </button>
      </div>
    </div>
  );
}
